import React, { Fragment, useState } from "react";
import { Button, Modal, Badge } from "react-bootstrap";
import swal from "sweetalert";
import {
  notifyTopRight,
  notifyError,
} from "../../toast/SetToast";
import Toast from "../../toast/SetToast";
import { db } from "../../../../firebase";
import {
  doc,
  getDoc,
  updateDoc,
  addDoc,
  collection,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import { useAuth } from "../../../../context/AuthContext";
import { createMultipleStockLogs } from "../../../../utils/stockLogs";
import {
  approveAccessoryMaterialIssue,
  formatLotAllocations,
} from "../../../../utils/accessoryLotInventory";

const getStatusBadge = (status) => {
  switch (status) {
    case "Pending":
      return "warning";
    case "Approved":
      return "success";
    case "Rejected":
      return "danger";
    case "Cancelled":
      return "dark";
    default:
      return "secondary";
  }
};

const MaterialIssueDetail = (props) => {
  const { currentUser } = useAuth();
  const { item, isApprover } = props;
  const [rejectionReason, setRejectionReason] = useState("");
  const [showRejectInput, setShowRejectInput] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const verifyApproverStatus = async () => {
    try {
      const uidQ = query(
        collection(db, "approvers"),
        where("uid", "==", currentUser.uid)
      );
      const uidSnap = await getDocs(uidQ);

      for (const docSnap of uidSnap.docs) {
        const data = docSnap.data();
        const isCurrentUser =
          data.name === currentUser.displayName ||
          data.email === currentUser.email;
        if (isCurrentUser && data.status === "Available") return true;
      }

      // Fallback: match by displayName (for older docs without uid)
      if (currentUser.displayName) {
        const nameQ = query(
          collection(db, "approvers"),
          where("name", "==", currentUser.displayName)
        );
        const nameSnap = await getDocs(nameQ);
        for (const docSnap of nameSnap.docs) {
          if (docSnap.data().status === "Available") return true;
        }
      }

      return false;
    } catch (err) {
      console.error("Error verifying approver status:", err);
      return false;
    }
  };

  const handleOnHideModal = () => {
    setShowRejectInput(false);
    setRejectionReason("");
    props.hide();
  };

  const handleApprove = async () => {
    const stillApprover = await verifyApproverStatus();
    if (!stillApprover) {
      notifyError("You are no longer an available approver.");
      return;
    }

    swal({
      title: "Approve this request?",
      text: "This will adjust the inventory accordingly.",
      icon: "info",
      buttons: true,
    }).then(async (willApprove) => {
      if (!willApprove) return;

      setIsProcessing(true);
      try {
        // Re-verify right before writing
        const recheck = await verifyApproverStatus();
        if (!recheck) {
          notifyError("You are no longer an available approver.");
          return;
        }

        if (item.materialType === "Accessory") {
          await approveAccessoryMaterialIssue(db, item.id, currentUser);
        } else if (item.materialType === "CPAP") {
          await approveCPAP();
          await updateDoc(doc(db, "materialIssues", item.id), {
            status: "Approved",
            approvedBy: currentUser.displayName || currentUser.email,
            approvedAt: new Date(),
            updatedAt: new Date(),
          });
        }

        notifyTopRight("อนุมัติสำเร็จ");
        handleOnHideModal();
      } catch (err) {
        console.error(err);
        notifyError("เกิดข้อผิดพลาด: " + err.message);
      } finally {
        setIsProcessing(false);
      }
    });
  };

  const approveCPAP = async () => {
    const productRef = doc(db, "products", item.itemId);
    const productDoc = await getDoc(productRef);

    if (!productDoc.exists()) {
      throw new Error("Product not found");
    }

    const productData = productDoc.data();

    if (productData.status === "ขาย" || productData.status === "เครื่องทดลอง") {
      throw new Error(
        `Cannot issue this product. Current status: ${productData.status}`
      );
    }

    // Update product status
    await updateDoc(productRef, {
      status: "เครื่องทดลอง",
    });

    // Create log entry in products/{id}/logs
    await addDoc(collection(db, "products", item.itemId, "logs"), {
      activity: `Material Issue: ${item.requestNo}`,
      status: "เครื่องทดลอง",
      notes: item.reason,
      createdBy: currentUser.displayName || currentUser.email,
      createdAt: new Date(),
    });

    // Create stock log entry (withdraw style)
    await createMultipleStockLogs([
      {
        itemId: item.itemId,
        itemName: item.itemName,
        itemType: "CPAP",
        quantity: -1,
        stockBefore: 1,
        stockAfter: 0,
        type: "withdraw",
        notes: `Material Issue Request: ${item.requestNo} - ${item.reason}`,
        createdBy: currentUser.displayName || currentUser.email,
      },
    ]);
  };

  const handleCancel = async () => {
    swal({
      title: "Cancel this request?",
      text: "This will cancel your material issue request.",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then(async (willCancel) => {
      if (!willCancel) return;

      setIsProcessing(true);
      try {
        await updateDoc(doc(db, "materialIssues", item.id), {
          status: "Cancelled",
          cancelledBy: currentUser.displayName || currentUser.email,
          cancelledAt: new Date(),
          updatedAt: new Date(),
        });

        notifyTopRight("ยกเลิกคำขอสำเร็จ");
        handleOnHideModal();
      } catch (err) {
        console.error(err);
        notifyError("เกิดข้อผิดพลาด: " + err.message);
      } finally {
        setIsProcessing(false);
      }
    });
  };

  const handleReject = async () => {
    const stillApprover = await verifyApproverStatus();
    if (!stillApprover) {
      notifyError("You are no longer an available approver.");
      return;
    }

    if (!rejectionReason.trim()) {
      notifyError("Please enter a rejection reason");
      return;
    }

    swal({
      title: "Reject this request?",
      text: "No inventory changes will be made.",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then(async (willReject) => {
      if (!willReject) return;

      setIsProcessing(true);
      try {
        // Re-verify right before writing
        const recheck = await verifyApproverStatus();
        if (!recheck) {
          notifyError("You are no longer an available approver.");
          return;
        }

        await updateDoc(doc(db, "materialIssues", item.id), {
          status: "Rejected",
          rejectedBy: currentUser.displayName || currentUser.email,
          rejectedAt: new Date(),
          rejectionReason: rejectionReason,
          updatedAt: new Date(),
        });

        notifyTopRight("ปฏิเสธคำขอสำเร็จ");
        handleOnHideModal();
      } catch (err) {
        console.error(err);
        notifyError("เกิดข้อผิดพลาด: " + err.message);
      } finally {
        setIsProcessing(false);
      }
    });
  };

  if (!item) return null;

  const requestedAt = item.requestedAt
    ? item.requestedAt.toDate
      ? item.requestedAt.toDate()
      : new Date(item.requestedAt)
    : null;

  const approvedAt = item.approvedAt
    ? item.approvedAt.toDate
      ? item.approvedAt.toDate()
      : new Date(item.approvedAt)
    : null;

  const rejectedAt = item.rejectedAt
    ? item.rejectedAt.toDate
      ? item.rejectedAt.toDate()
      : new Date(item.rejectedAt)
    : null;

  const cancelledAt = item.cancelledAt
    ? item.cancelledAt.toDate
      ? item.cancelledAt.toDate()
      : new Date(item.cancelledAt)
    : null;

  const isRequester =
    currentUser &&
    (currentUser.email === item.requestedByEmail ||
      currentUser.displayName === item.requestedBy);

  return (
    <Fragment>
      <Toast />
      <Modal
        className="fade bd-example-modal-lg"
        show={props.show}
        onHide={handleOnHideModal}
        size="lg"
      >
        <Modal.Header>
          <Modal.Title>
            Material Issue Request: {item.requestNo}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="table-responsive">
            <table className="table table-bordered">
              <tbody>
                <tr>
                  <td className="font-weight-bold" style={{ width: "30%" }}>
                    Request No
                  </td>
                  <td>{item.requestNo}</td>
                </tr>
                <tr>
                  <td className="font-weight-bold">Status</td>
                  <td>
                    <Badge variant={getStatusBadge(item.status)}>
                      {item.status}
                    </Badge>
                  </td>
                </tr>
                <tr>
                  <td className="font-weight-bold">Material Type</td>
                  <td>{item.materialType}</td>
                </tr>
                <tr>
                  <td className="font-weight-bold">Item Name</td>
                  <td>{item.itemName}</td>
                </tr>
                <tr>
                  <td className="font-weight-bold">Item Details</td>
                  <td>{item.itemDetails}</td>
                </tr>
                <tr>
                  <td className="font-weight-bold">Quantity</td>
                  <td>{item.quantity}</td>
                </tr>
                {item.lotAllocations && item.lotAllocations.length > 0 && (
                  <tr>
                    <td className="font-weight-bold">Lots</td>
                    <td>{formatLotAllocations(item.lotAllocations)}</td>
                  </tr>
                )}
                <tr>
                  <td className="font-weight-bold">Reason</td>
                  <td>{item.reason}</td>
                </tr>
                <tr>
                  <td className="font-weight-bold">Requested By</td>
                  <td>
                    {item.requestedBy} ({item.requestedByEmail})
                  </td>
                </tr>
                <tr>
                  <td className="font-weight-bold">Requested At</td>
                  <td>
                    {requestedAt
                      ? requestedAt.toLocaleDateString("th-TH", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })
                      : "-"}
                  </td>
                </tr>
                {item.status === "Approved" && (
                  <>
                    <tr>
                      <td className="font-weight-bold">Approved By</td>
                      <td>{item.approvedBy}</td>
                    </tr>
                    <tr>
                      <td className="font-weight-bold">Approved At</td>
                      <td>
                        {approvedAt
                          ? approvedAt.toLocaleDateString("th-TH", {
                              day: "2-digit",
                              month: "2-digit",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            })
                          : "-"}
                      </td>
                    </tr>
                  </>
                )}
                {item.status === "Rejected" && (
                  <>
                    <tr>
                      <td className="font-weight-bold">Rejected By</td>
                      <td>{item.rejectedBy}</td>
                    </tr>
                    <tr>
                      <td className="font-weight-bold">Rejected At</td>
                      <td>
                        {rejectedAt
                          ? rejectedAt.toLocaleDateString("th-TH", {
                              day: "2-digit",
                              month: "2-digit",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            })
                          : "-"}
                      </td>
                    </tr>
                    <tr>
                      <td className="font-weight-bold">Rejection Reason</td>
                      <td>{item.rejectionReason}</td>
                    </tr>
                  </>
                )}
                {item.status === "Cancelled" && (
                  <>
                    <tr>
                      <td className="font-weight-bold">Cancelled By</td>
                      <td>{item.cancelledBy}</td>
                    </tr>
                    <tr>
                      <td className="font-weight-bold">Cancelled At</td>
                      <td>
                        {cancelledAt
                          ? cancelledAt.toLocaleDateString("th-TH", {
                              day: "2-digit",
                              month: "2-digit",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            })
                          : "-"}
                      </td>
                    </tr>
                  </>
                )}
              </tbody>
            </table>
          </div>

          {/* Reject reason input */}
          {showRejectInput && (
            <div className="form-group mt-3">
              <label className="font-weight-bold">
                Rejection Reason<span className="text-danger">*</span>
              </label>
              <textarea
                className="form-control"
                rows="3"
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Enter reason for rejection..."
              />
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          {/* Approve/Reject buttons only for pending items and available approvers */}
          {item.status === "Pending" && isApprover && (
            <>
              {!showRejectInput ? (
                <>
                  <Button
                    variant="success"
                    onClick={handleApprove}
                    disabled={isProcessing}
                  >
                    {isProcessing ? "Processing..." : "Approve"}
                  </Button>
                  <Button
                    variant="danger"
                    onClick={() => setShowRejectInput(true)}
                    disabled={isProcessing}
                  >
                    Reject
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="danger"
                    onClick={handleReject}
                    disabled={isProcessing}
                  >
                    {isProcessing ? "Processing..." : "Confirm Reject"}
                  </Button>
                  <Button
                    variant="light"
                    onClick={() => {
                      setShowRejectInput(false);
                      setRejectionReason("");
                    }}
                    disabled={isProcessing}
                  >
                    Back
                  </Button>
                </>
              )}
            </>
          )}
          <Button variant="secondary" onClick={handleOnHideModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Fragment>
  );
};

export default MaterialIssueDetail;
